import React, { FC, useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { graphic, EChartsOption } from "echarts";

type Props = {
  xAxisData: (number | string)[];
  seriesData: (number | string)[];
  color?: (string | graphic.LinearGradient | graphic.LinearGradient)[];
  tooltipValueName: string;
  borderColor: string;
  isDollarInSeries?: boolean;
  height?: string;
  title?: string;
};

const BarChart: FC<Props> = ({
  xAxisData,
  seriesData = [],
  borderColor = "#007DF299",
  title,
  isDollarInSeries = false,
  color = [
    new graphic.LinearGradient(1, 0, 0, 1, [
      { offset: 0, color: "rgba(0, 125, 242, 0.5)" },
      { offset: 1, color: "rgba(0, 125, 242, 0.15)" },
    ]),
  ],
  tooltipValueName,
  height = "300px",
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [theme, setTheme] = useState<string | null>(null);
  const numericSeriesData = seriesData?.map((value) =>
    typeof value === "string" ? Number(value.replace(/[$,]/g, "")) : value
  );

  useEffect(() => {
    const currentTheme = document.documentElement.dataset.theme;
    setTheme(currentTheme ? currentTheme : null);
  }, []);

  useEffect(() => {
    if (chartRef.current) {
      const myChart = echarts.init(chartRef.current);

      const option: EChartsOption = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: isDollarInSeries
            ? (params: any) => {
                const value = params[0].value;
                const formattedValue = isDollarInSeries ? `$${value}` : value;
                return ` ${params[0].name}<br/>${tooltipValueName}: ${formattedValue}`;
              }
            : undefined,
        },
        title: {
          text: title,
          textStyle: {
            color: theme === "light" ? "#5B5B5B" : "white",
            fontSize: 18,
            fontWeight: 500,
          },
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: [
          {
            type: "category",
            data: xAxisData,
            axisTick: {
              alignWithLabel: true,
            },
            axisLine: {
              lineStyle: {
                type: "solid",
                width: 2,
                color: theme === "light" ? "#5B5B5B" : "white",
                opacity: 1,
              },
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            splitLine: {
              lineStyle: {
                type: "dashed",
                width: 1,
                color: "#5B5B5B",
                join: "round",
                cap: "butt",
              },
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: theme === "light" ? "#5B5B5B" : "white",
                width: 2,
              },
            },
            axisLabel: {
              formatter: (value: number) =>
                isDollarInSeries
                  ? `$${value.toLocaleString()}`
                  : value.toLocaleString(),
            },
          },
        ],
        series: [
          {
            name: tooltipValueName,
            type: "bar",
            barWidth: "50%",
            data: numericSeriesData,
            itemStyle: {
              borderColor,
              color: (params: { dataIndex: number }) =>
                color[params.dataIndex % color.length],
              borderRadius: 11,
            },
          },
        ],
      };

      myChart.setOption(option);

      return () => {
        myChart.dispose();
      };
    }
  }, [
    xAxisData,
    numericSeriesData,
    color,
    borderColor,
    tooltipValueName,
    isDollarInSeries,
    title,
  ]);

  return <div ref={chartRef} style={{ width: "100%", height }} />;
};

export default BarChart;
